import React from 'react';
import { AlertTriangle } from 'lucide-react';
import type { Product } from '../../../../types/product';
import { formatCompactNumber } from '../../../../utils/numberFormatter';

export interface DiscontinueConfirmationModalProps {
  product: Product;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Modal component for confirming product discontinuation.
 * Shows impact information and requires explicit confirmation.
 *
 * **Integration:** This modal is designed to be triggered from the product pipeline page
 * when users attempt to discontinue a product. It should be wrapped in the shared-ui
 * Modal component which handles backdrop clicks and Escape key handling.
 *
 * @see Issue #121 - feat: discontinue product confirmation popup
 */
export function DiscontinueConfirmationModal({
  product,
  onConfirm,
  onCancel,
}: DiscontinueConfirmationModalProps) {

  return (
    <>
      <div className="bg-theme-error p-4">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-lg font-medium text-white">
            Discontinue Product?
          </h3>
        </div>
      </div>

      <div className="p-6">
        <p className="text-theme-text-secondary mb-4">
          Are you sure you want to discontinue <strong className="text-theme-text-primary">{product.name}</strong>?
        </p>

        <div className="bg-theme-bg-secondary p-4 rounded-lg mb-6">
          <h4 className="font-medium text-theme-text-primary mb-3">Impact Summary:</h4>
          <ul className="text-sm text-theme-text-secondary space-y-2">
            <li>• <strong>Current Sales:</strong> {formatCompactNumber(product.salesMetrics?.unitsSold ?? 0)} units sold</li>
            <li>• <strong>Revenue Lost:</strong> Future sales from this product will cease</li>
            <li>• <strong>Market Share:</strong> {((product.salesMetrics?.marketShare ?? 0) * 100).toFixed(1)}% will be lost</li>
            <li>• <strong>Status:</strong> Product will be moved to discontinued state</li>
          </ul>
        </div>

        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 rounded-lg mb-6">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            <strong>Warning:</strong> This action cannot be undone. The product will no longer be available for sale.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-3 bg-theme-bg-tertiary text-theme-text-primary rounded-lg hover:bg-theme-bg-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-theme-accent focus:ring-offset-2"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 px-4 py-3 bg-theme-error text-white rounded-lg hover:bg-theme-error/90 transition-colors focus:outline-none focus:ring-2 focus:ring-theme-error focus:ring-offset-2"
          >
            Discontinue Product
          </button>
        </div>

      </div>
    </>
  );
}
